/**
 * Account pages (sign-in forms and the account panel): password visibility,
 * the submitting state of forms, resend cooldowns, server toasts, the panel
 * tab strip, and dialogs in place of window.confirm/prompt/alert.
 */
import { pluralPl } from '../shared/polish-plural';
import { confirmDialog, promptDialog, showToast, dismissToast, openModal } from '../shared/dialog';
import { initPasswordToggle, passwordInputOf } from '../shared/password-toggle';

const TOAST_TIMEOUT_MS = 5000;
const FAVORITE_UNDO_MS = 5000;

// „Pokaż hasło” of every password Field (views/user-account/_field.php), with the names of its markup.
function initPasswordToggles() {
    document.querySelectorAll('[data-password-toggle]').forEach((button) => {
        initPasswordToggle(button, passwordInputOf(button));
    });
}

function initSubmittingForms() {
    document.querySelectorAll('form[data-submitting-label]').forEach((form) => {
        form.addEventListener('submit', (event) => {
            if (form.dataset.submitting === '1') {
                event.preventDefault();
                return;
            }
            form.dataset.submitting = '1';
            const button = event.submitter || form.querySelector('[type="submit"]');
            // Disable after the browser has collected the form data.
            window.setTimeout(() => {
                form.querySelectorAll('input:not([type="hidden"]), textarea').forEach((field) => { field.readOnly = true; });
                if (button) {
                    button.disabled = true;
                    button.classList.add('is-loading');
                    button.textContent = form.dataset.submittingLabel;
                }
            }, 0);
        });
    });
}

function initCooldowns() {
    document.querySelectorAll('[data-cooldown-seconds]').forEach((button) => {
        let remaining = parseInt(button.getAttribute('data-cooldown-seconds') || '0', 10);
        const label = button.getAttribute('data-cooldown-label') || button.textContent.trim();
        const render = () => {
            if (remaining <= 0) {
                button.disabled = false;
                button.textContent = label;
                return;
            }
            button.disabled = true;
            if (remaining > 90) {
                const minutes = Math.ceil(remaining / 60);
                button.textContent = `${label} za ${minutes} ${pluralPl(minutes, ['minutę', 'minuty', 'minut'])}`;
            } else {
                button.textContent = `${label} za ${remaining} ${pluralPl(remaining, ['sekundę', 'sekundy', 'sekund'])}`;
            }
        };
        render();
        if (remaining > 0) {
            const timer = window.setInterval(() => {
                remaining -= 1;
                render();
                if (remaining <= 0) window.clearInterval(timer);
            }, 1000);
        }
    });
}

function initServerToasts() {
    document.querySelectorAll('[data-toast]').forEach((toast) => {
        toast.querySelector('[data-toast-close]')?.addEventListener('click', () => dismissToast(toast));
        window.setTimeout(() => dismissToast(toast), TOAST_TIMEOUT_MS);
    });
}

function initTabs() {
    document.querySelectorAll('[data-account-tabs]').forEach((tabs) => {
        const active = tabs.querySelector('[aria-current="page"]');
        if (active && tabs.scrollWidth > tabs.clientWidth) {
            tabs.scrollLeft = Math.max(0, active.offsetLeft - 16);
        }
    });
}

function initFocusSummary() {
    const summary = document.querySelector('[data-error-summary]');
    if (summary) summary.focus();
}

/** Yii's data-confirm handler calls yii.confirm with the clicked element as `this`. */
function initConfirmations() {
    if (!window.yii) return;
    window.yii.confirm = function (message, ok, cancel) {
        const element = this instanceof Element ? this : null;
        const title = element?.getAttribute('data-confirm-title') || message;
        const body = element?.getAttribute('data-confirm-title') ? message : '';
        confirmDialog({
            title,
            body,
            okLabel: element?.getAttribute('data-confirm-ok') || 'Potwierdź',
            tone: element?.getAttribute('data-confirm-tone') || 'primary',
        }).then((confirmed) => {
            if (confirmed) { if (ok) ok(); } else if (cancel) cancel();
        });
    };
}

function apiRequest(url, method, body) {
    const headers = { Accept: 'application/json' };
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
    if (csrf) headers['X-CSRF-Token'] = csrf;
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    return fetch(url, {
        method,
        credentials: 'same-origin',
        keepalive: method === 'DELETE',
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });
}

function applyFavoriteComment(edit, comment) {
    edit.setAttribute('data-favorite-comment', comment);
    const target = edit.closest('[data-favorite-card]')?.querySelector('[data-favorite-comment-text]');
    if (target) {
        target.textContent = comment;
        target.hidden = comment.trim() === '';
    }
}

function initFavorites() {
    const pending = new Map();
    const flush = (id) => {
        const entry = pending.get(id);
        if (!entry) return;
        pending.delete(id);
        window.clearTimeout(entry.timer);
        apiRequest(`/rcn/api/favorites/${id}`, 'DELETE').then((response) => {
            if (!response.ok) throw new Error();
            entry.card.remove();
        }).catch(() => {
            entry.card.hidden = false;
            showToast('Nie udało się usunąć z ulubionych. Spróbuj ponownie.');
        });
    };

    document.addEventListener('click', async (event) => {
        const remove = event.target.closest('[data-favorite-delete]');
        if (remove) {
            event.preventDefault();
            const id = remove.getAttribute('data-favorite-delete');
            const card = remove.closest('[data-favorite-card]');
            if (!card || pending.has(id)) return;
            card.hidden = true;
            const timer = window.setTimeout(() => flush(id), FAVORITE_UNDO_MS);
            pending.set(id, { card, timer });
            showToast('Usunięto z ulubionych.', {
                actionLabel: 'Cofnij',
                timeout: FAVORITE_UNDO_MS,
                onAction: () => {
                    const entry = pending.get(id);
                    if (!entry) return;
                    window.clearTimeout(entry.timer);
                    pending.delete(id);
                    entry.card.hidden = false;
                },
            });
            return;
        }

        const edit = event.target.closest('[data-favorite-edit]');
        if (edit) {
            event.preventDefault();
            const id = edit.getAttribute('data-favorite-edit');
            const saved = await promptDialog({
                title: 'Komentarz do ulubionego miejsca',
                body: 'Komentarz widzisz tylko Ty.',
                okLabel: 'Zapisz komentarz',
                submittingLabel: 'Zapisywanie komentarza…',
                field: { label: 'Komentarz', value: edit.getAttribute('data-favorite-comment') || '', maxLength: 2000 },
                onSubmit: async (value) => {
                    const response = await apiRequest(`/rcn/api/favorites/${id}`, 'PATCH', { comment: value });
                    if (!response.ok) throw new Error('Nie udało się zapisać komentarza. Spróbuj ponownie.');
                    // The saved text belongs to this favourite, whatever the dialog shows by now.
                    applyFavoriteComment(edit, value);
                },
            });
            if (saved !== null) showToast('Komentarz został zapisany.');
        }
    });

    window.addEventListener('pagehide', () => { Array.from(pending.keys()).forEach(flush); });
}

function initReportCancel() {
    document.addEventListener('click', async (event) => {
        const button = event.target.closest('[data-report-cancel]');
        if (!button) return;
        event.preventDefault();
        const confirmed = await confirmDialog({
            title: 'Anulować zgłoszenie?',
            body: 'Moderatorzy nie zajmą się tym zgłoszeniem. Sygnał pozostanie na mapie bez zmian.',
            okLabel: 'Anuluj zgłoszenie',
            cancelLabel: 'Wróć',
            tone: 'danger',
        });
        if (!confirmed) return;
        const id = button.getAttribute('data-report-cancel');
        const signalId = button.getAttribute('data-signal-id');
        const response = await apiRequest(`/rcn/api/community-signals/${signalId}/report/${id}`, 'DELETE').catch(() => null);
        if (response && response.ok) {
            window.location.reload();
            return;
        }
        showToast('Nie udało się anulować zgłoszenia.');
    });
}

// [data-modal-open="<id>"] opens a server-rendered .ms-modal through the shared
// dialog runtime (focus trap, Escape, focus back on the trigger).
function initModalTriggers() {
    document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-modal-open]');
        if (!trigger) return;
        const modal = document.getElementById(trigger.getAttribute('data-modal-open'));
        if (!modal) return;
        event.preventDefault();
        openModal(modal, { returnFocus: trigger });
    });
}

function initCharacterCounters() {
    document.querySelectorAll('[data-char-counter]').forEach((counter) => {
        const field = document.getElementById(counter.getAttribute('data-char-counter'));
        if (!field) return;
        const max = Number(field.getAttribute('maxlength')) || 0;
        const render = () => {
            const length = field.value.length;
            counter.textContent = `${length} z ${max.toLocaleString('pl-PL')} ${pluralPl(max, ['znaku', 'znaków', 'znaków'])}`;
        };
        field.addEventListener('input', render);
        render();
    });
}

function initRowDetails() {
    document.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-row-toggle]');
        if (!toggle) return;
        const row = document.getElementById(toggle.getAttribute('aria-controls'));
        if (!row) return;
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        row.hidden = !open;
        const label = toggle.querySelector('[data-row-toggle-label]');
        if (label) label.textContent = open ? 'Zwiń wykres' : 'Pokaż wykres';
    });
}

function initAutoSubmit() {
    document.querySelectorAll('[data-autosubmit]').forEach((control) => {
        control.addEventListener('change', () => {
            const form = control.form;
            if (form && typeof form.requestSubmit === 'function') form.requestSubmit();
            else if (form) form.submit();
        });
    });
}

// „⋯” row actions (details.ms-dropdown): a plain disclosure of links and
// buttons. A click outside closes it; Escape closes it and returns the focus to
// its summary. An open dialog handles its own Escape first (defaultPrevented).
function initDropdowns() {
    document.addEventListener('click', (event) => {
        document.querySelectorAll('details.ms-dropdown[open]').forEach((dropdown) => {
            if (!dropdown.contains(event.target)) dropdown.removeAttribute('open');
        });
    });
    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || event.defaultPrevented) return;
        document.querySelectorAll('details.ms-dropdown[open]').forEach((dropdown) => {
            const hadFocus = dropdown.contains(document.activeElement);
            dropdown.removeAttribute('open');
            const summary = dropdown.querySelector('summary');
            if (hadFocus && summary) summary.focus();
        });
    });
}

initPasswordToggles();
initSubmittingForms();
initCooldowns();
initServerToasts();
initTabs();
initFocusSummary();
initConfirmations();
initFavorites();
initReportCancel();
initModalTriggers();
initCharacterCounters();
initRowDetails();
initAutoSubmit();
initDropdowns();
